<template>
  <div class="flex flex-row items-center gap-4" data-avatar-shape="square">
    <UserAvatar
      :user-id="user.id"
      size="large"
      :blink-icon="blinkIcon"
      :blink-icon-enabled="!!blinkIcon"
      :disponibility="user.disponibility?.assignement_status"
      hide-status
      flat
    />
    <div class="flex-1 flex flex-col gap-0.5">
      <h3 class="text-base leading-none font-bold">
        {{ user.full_name }}
      </h3>
      <span
        v-if="assignedFunction"
        :data-tint="assignedFunction.tint"
        class="bg-primary text-contrast-primary self-start rounded px-1 text-xs"
      >
        {{ assignedFunction.name }}
      </span>
      <div v-if="hasAvailabilityItems" class="flex w-full flex-col">
        <table class="border-separate border-spacing-y-1.5">
          <tbody>
            <AvailabilityItem
              v-for="(availabilityItem, key) in localAvailabilityItems"
              :key="key"
              :item="availabilityItem"
            />
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="editionMode" class="shrink-0">
      <BaseDropDownMenu size="small" :options="actionMenuOptions" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import UserAvatar from '@c/Avatar/UserAvatar.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import AvailabilityItem from '@c/Dashboard/Content/Planning/components/AvailabilityItem.vue'

export default defineComponent({
  name: 'SelectedAssignedUser',
  components: { AvailabilityItem, UserAvatar, BaseDropDownMenu },
  props: {
    user: {
      type: Object,
      required: true
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    fixedFunction: {
      type: String,
      required: false,
      default: null
    },
    allUsersAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['update-disponibility', 'remove'],
  computed: {
    ...mapGetters({
      assignedPersonFunctions: 'metadata/getAssignedPersonFunctionsMetadata',
      assignementStatuses: 'metadata/getAssignmentStatusMetadata'
    }),
    localAvailabilityItems() {
      return this.allUsersAvailabilityItems.filter(
        (item) => item.model_id === this.user.id
      )
    },
    hasAvailabilityItems() {
      return this.localAvailabilityItems.length > 0
    },
    assignedFunction() {
      return this.assignedPersonFunctions.find(
        (f) => f.id === (this.fixedFunction ?? this.user.disponibility?.function)
      )
    },
    blinkIcon() {
      return this.assignedFunction?.icon ?? null
    },
    actionMenuOptions() {
      const groups = [
        this.assignementStatuses.map((status) => ({
          label: status.name,
          icon: status.icon ?? 'user',
          gate: true,
          tint: status.bg_color,
          bg_color:
            this.user.disponibility?.assignement_status === status.id
              ? 'bg-primary'
              : 'bg-white',
          txt_color:
            this.user.disponibility?.assignement_status === status.id
              ? 'text-contrast-primary'
              : 'text-primary',
          action: () =>
            this.$emit('update-disponibility', { assignement_status: status.id })
        }))
      ]

      groups.push(
        this.assignedPersonFunctions.map((fn) => ({
          label: fn.name,
          icon: fn.icon ?? 'user',
          gate: true,
          tint: fn.tint,
          disabled: !!this.fixedFunction,
          bg_color:
            (this.fixedFunction ?? this.user.disponibility?.function) === fn.id
              ? 'bg-primary'
              : 'bg-white',
          txt_color:
            (this.fixedFunction ?? this.user.disponibility?.function) === fn.id
              ? 'text-contrast-primary'
              : 'text-primary',
          action: () => this.$emit('update-disponibility', { function: fn.id })
        }))
      )

      groups.push([
        {
          label: this.$t('button.delete'),
          icon: 'trash',
          gate: true,
          bg_color: 'bg-red-500',
          txt_color: 'text-white',
          action: () => this.$emit('remove')
        }
      ])

      return groups
    }
  }
})
</script>